import { z } from 'zod';
import { LocaleSchema } from './i18n';
import { CurrencySchema } from './money';
import { MeResellerSchema } from './reseller';

export const SITE_IDS = ['cn', 'global'] as const;
export type SiteId = (typeof SITE_IDS)[number];

export const PublicSiteSchema = z.object({
  site_id: z.enum(SITE_IDS),
  name: z.string(),
  currency: CurrencySchema,
  timezone: z.string(),
  locale: z.string(),
  api_base_url: z.string(),
  other_site_url: z.string().nullable(),
  contact: z.object({ email: z.string().nullable(), wechat: z.string().nullable(), note: z.string().nullable() }),
  announcement: z.string().nullable(),
  /** Cloudflare Turnstile 站点密钥；null 表示未启用人机验证（本地开发） */
  /** 人机验证方式：pow = 自建计算验证（前端从 /portal/public/human-challenge 取题自动求解）；turnstile；null = 不验证 */
  human_check: z.enum(['pow', 'turnstile']).nullable(),
  /** 仅 human_check 为 turnstile 时有值 */
  turnstile_site_key: z.string().nullable(),
});
export type PublicSite = z.infer<typeof PublicSiteSchema>;

export const PageQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  after: z.string().optional(),
});

export interface Page<T> {
  data: T[];
  has_more: boolean;
  next_cursor: string | null;
}

const EmailCodeSchema = z.string().regex(/^\d{6}$/);
/** 人机验证令牌（字段名沿用 turnstile_token）：自建计算验证的解（pow1. 开头）或 Turnstile 令牌；单次有效 */
const TurnstileTokenSchema = z.string().max(2048).optional();

/** POST /portal/auth/email-code：发送注册或登录验证码（不论邮箱是否注册都返回 ok，不暴露存在性） */
/** GET /portal/public/human-challenge：自建计算验证的题目（ALTCHA 式） */
export const HumanChallengeSchema = z.object({
  algorithm: z.literal('SHA-256'),
  challenge: z.string(),
  salt: z.string(),
  maxnumber: z.number().int(),
  signature: z.string(),
});
export type HumanChallenge = z.infer<typeof HumanChallengeSchema>;

export const SendEmailCodeSchema = z.object({
  email: z.email().max(254),
  purpose: z.enum(['register', 'login']),
  turnstile_token: TurnstileTokenSchema,
  /** 可选界面语言：决定验证码邮件的语言；缺省时取 Accept-Language，仍无法识别则用站点默认语言 */
  locale: LocaleSchema.optional(),
});
/** POST /portal/auth/register：邮箱 + 验证码注册（不设密码，注册后可在账号设置中设置） */
export const RegisterSchema = z.object({
  email: z.email().max(254),
  code: EmailCodeSchema,
  /** 可选推荐码（大小写不敏感）；无效时返回 referral_code 字段错误 */
  referral_code: z.string().trim().max(32).nullable().optional(),
  /** 可选渠道推广码（来自渠道链接 ?ch=）；无效或渠道已停用时忽略，不影响注册 */
  channel_code: z.string().trim().max(32).nullable().optional(),
  /** 可选界面语言：保存为用户的语言偏好 */
  locale: LocaleSchema.optional(),
  /** 必须同意《服务条款》与《隐私政策》（服务端记录同意时的版本） */
  accept_terms: z.literal(true),
});
/** 法律文档：服务条款、隐私政策（GET /portal/public/legal/:doc） */
export const LEGAL_DOCS = ['terms', 'privacy'] as const;
export type LegalDoc = (typeof LEGAL_DOCS)[number];
export const PublicLegalDocSchema = z.object({ doc: z.enum(LEGAL_DOCS), version: z.number().int(), body: z.string() });
export type PublicLegalDoc = z.infer<typeof PublicLegalDocSchema>;
/** POST /portal/auth/login：邮箱 + 密码 */
export const LoginSchema = z.object({ email: z.email(), password: z.string().min(1).max(128), turnstile_token: TurnstileTokenSchema });
/** POST /portal/auth/login-code：邮箱 + 验证码 */
export const LoginCodeSchema = z.object({ email: z.email(), code: EmailCodeSchema });
export const ForgotPasswordSchema = z.object({ email: z.email(), turnstile_token: TurnstileTokenSchema });
export const ResetPasswordSchema = z.object({
  email: z.email(),
  code: EmailCodeSchema,
  password: z.string().min(10).max(128),
});
/** POST /portal/me/password：已有密码时必须提供 current_password；还没设过密码时省略 */
export const ChangePasswordSchema = z.object({
  current_password: z.string().min(1).optional(),
  new_password: z.string().min(10).max(128),
});

/** 两步验证动态码（验证器 App 上的 6 位数字） */
const TotpCodeSchema = z.string().regex(/^\d{6}$/);
/** POST /portal/auth/login：开启了两步验证的账号不直接登录，而是返回挑战，再用 /auth/login-mfa 完成 */
export const MfaChallengeSchema = z.object({ mfa_required: z.literal(true), mfa_token: z.string() });
export type MfaChallenge = z.infer<typeof MfaChallengeSchema>;
/** POST /portal/auth/login-mfa */
/** 恢复码：开启两步验证时发放 10 个，一次性；不区分大小写，可带空格或连字符 */
const RecoveryCodeSchema = z.string().trim().min(8).max(32);
/** 动态码与恢复码二选一 */
const exactlyOneProof = (v: { code?: string; recovery_code?: string }) => Boolean(v.code) !== Boolean(v.recovery_code);
/** POST /portal/auth/login-mfa：密码或邮箱验证码登录后，开启了两步验证的账号用动态码（或恢复码）完成登录 */
export const LoginMfaSchema = z
  .object({ mfa_token: z.string().min(16).max(128), code: TotpCodeSchema.optional(), recovery_code: RecoveryCodeSchema.optional() })
  .refine(exactlyOneProof, { message: '需要动态码或恢复码（二选一）', path: ['code'] });
/** POST /portal/me/mfa/setup：生成待确认的密钥（未开启时才可调用） */
export const MfaSetupSchema = z.object({ secret: z.string(), otpauth_url: z.string() });
export type MfaSetup = z.infer<typeof MfaSetupSchema>;
/** POST /portal/me/mfa/enable：用验证器上的动态码确认开启；返回 10 个恢复码（只返回这一次） */
export const EnableMfaSchema = z.object({ code: TotpCodeSchema });
export const MfaRecoveryCodesSchema = z.object({ recovery_codes: z.array(z.string()) });
export type MfaRecoveryCodes = z.infer<typeof MfaRecoveryCodesSchema>;
/** POST /portal/me/mfa/recovery-codes：用动态码换一组新的恢复码（旧的全部作废） */
export const RegenerateRecoveryCodesSchema = z.object({ code: TotpCodeSchema });
/** POST /portal/me/mfa/disable：动态码或恢复码二选一（不接受邮箱验证码：邮箱被盗时两步验证仍要有效） */
export const DisableMfaSchema = z
  .object({ code: TotpCodeSchema.optional(), recovery_code: RecoveryCodeSchema.optional() })
  .refine(exactlyOneProof, { message: '需要动态码或恢复码（二选一）', path: ['code'] });

export const MeSchema = z.object({
  id: z.string(),
  email: z.string(),
  email_verified: z.boolean(),
  /** 是否已设置登录密码（验证码注册的账号默认没有） */
  has_password: z.boolean(),
  /** 是否开启了两步验证（TOTP）：开启后不论密码还是邮箱验证码登录，都要再输入动态码（或恢复码） */
  mfa_enabled: z.boolean(),
  /** 剩余可用的恢复码数量；未开启两步验证时为 null */
  mfa_recovery_codes_left: z.number().int().nullable(),
  status: z.enum(['active', 'suspended', 'closed']),
  created_at: z.string(),
  webhook_secret_set: z.boolean(),
  /** 用户保存的语言偏好；null 表示未设置（邮件用站点默认语言） */
  locale: LocaleSchema.nullable(),
  /** 渠道后台成员身份（docs/12）；非渠道成员为 null */
  reseller: MeResellerSchema.nullable(),
  csrf_token: z.string(),
});
export type Me = z.infer<typeof MeSchema>;

/** PUT /portal/me/locale */
export const UpdateLocaleSchema = z.object({ locale: LocaleSchema });
export type UpdateLocale = z.infer<typeof UpdateLocaleSchema>;

export const OverviewSchema = z.object({
  quota: z.object({
    currency: CurrencySchema,
    available: z.string(),
    reserved: z.string(),
    granted_total: z.string(),
    settled_total: z.string(),
  }),
  spend_daily: z.array(z.object({ day: z.string(), amount: z.string(), tasks: z.number() })),
  counts: z.object({ succeeded: z.number(), failed: z.number(), in_progress: z.number() }),
  onboarding: z.object({
    email_verified: z.boolean(),
    has_quota: z.boolean(),
    has_key: z.boolean(),
    has_asset: z.boolean(),
    has_task: z.boolean(),
  }),
});
export type Overview = z.infer<typeof OverviewSchema>;

export const RequestLogSchema = z.object({
  request_id: z.string(),
  method: z.string(),
  route: z.string(),
  status: z.number(),
  error_code: z.string().nullable(),
  api_key: z.string().nullable(),
  task_id: z.string().nullable(),
  latency_ms: z.number(),
  billable: z.boolean(),
  created_at: z.string(),
});
export type RequestLog = z.infer<typeof RequestLogSchema>;

export type TaskWithKey = import('./tasks').TaskObject & {
  api_key: { id: string; name: string; display: string } | null;
};
export type TaskDetail = TaskWithKey & { timeline: import('./tasks').CustomerTimelineStep[] };
export type PortalModel = import('./models').PublicModel & { granted: boolean };
